import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaHandshake,
  FaUsers,
  FaBalanceScale,
  FaSyncAlt,
  FaLightbulb,
} from 'react-icons/fa';
import teamImage from '../../assets/images/data.png';

const AboutSection: React.FC = () => {
  const { t } = useTranslation();

  const valeurs = [
    {
      icon: <FaHandshake className="text-3xl" />,
      title: t('about.valeurs.engagement'),
      desc: t('about.valeurs.desc_engagement'),
    },
    {
      icon: <FaUsers className="text-3xl" />,
      title: t('about.valeurs.competence'),
      desc: t('about.valeurs.desc_competence'),
    },
    {
      icon: <FaBalanceScale className="text-3xl" />,
      title: t('about.valeurs.rigueur'),
      desc: t('about.valeurs.desc_rigueur'),
    },
    {
      icon: <FaSyncAlt className="text-3xl" />,
      title: t('about.valeurs.agilite'),
      desc: t('about.valeurs.desc_agilite'),
    },
    {
      icon: <FaLightbulb className="text-3xl" />,
      title: t('about.valeurs.innovation'),
      desc: t('about.valeurs.desc_innovation'),
    },
  ];

  return (
    <section
      id="about"
      className="relative py-12 bg-gradient-to-t from-white via-blue-50 to-white overflow-hidden"
    >
      {/* Éléments décoratifs flottants */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="w-4 h-4 bg-[#4f837d]/30 rounded-full absolute top-16 left-1/5 animate-float" />
        <div className="w-3 h-3 bg-[#2e79b9]/30 rounded-full absolute bottom-24 right-1/4 animate-float delay-700" />
      </div>

      <div className="container mx-auto px-6 max-w-6xl relative z-10">
        {/* Titre principal avec couleur unie */}
        <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-16 text-[#155679] animate-fade-in">
          {t('about.title')}
          <span className="block w-24 h-1 mx-auto mt-4 bg-gradient-to-r from-[#4f837d] to-[#3a645f] rounded-full" />
        </h2>

        {/* Contenu principal */}
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Section texte avec effet verre dépoli */}
          <div className="relative bg-white/70 backdrop-blur-sm rounded-3xl p-8 shadow-lg animate-slide-up">
            <div className="absolute -top-3 -right-3 w-10 h-10 bg-[#4f837d]/40 rounded-full animate-pulse-slow" />
            <p className="text-lg text-gray-700 mb-6 leading-relaxed font-light">
              {t('about.description')}
            </p>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-[#4f837d] mb-2">{t('about.vision_title')}</h3>
                <p className="text-gray-600">{t('about.vision')}</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#4f837d] mb-2">{t('about.mission_title')}</h3>
                <p className="text-gray-600">{t('about.mission')}</p>
              </div>
            </div>
          </div>

          {/* Section image avec effet de carte */}
          <div className="relative h-80 group perspective-1000">
            <div className="absolute inset-0 bg-gradient-to-br from-[#4f837d]/10 to-[#2e79b9]/10 rounded-3xl transform rotate-y-4 transition-transform duration-700 group-hover:rotate-y-0 group-hover:shadow-xl">
              <img
                src={teamImage}
                alt={t('about.team_alt')}
                className="w-full h-full object-cover rounded-3xl group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#4f837d]/20 to-[#2e79b9]/20 rounded-3xl opacity-50 group-hover:opacity-30 transition-opacity duration-700" />
            </div>
            <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-[#2e79b9]/30 rounded-full animate-pulse-slow delay-500" />
          </div>
        </div>

        {/* Valeurs fondamentales */}
        <div className="mt-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-[#155679] animate-fade-in">
            {t('about.nos_valeurs')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {valeurs.map(({ icon, title, desc }, idx) => (
              <div
                key={idx}
                className="relative bg-white rounded-2xl p-6 shadow-md text-center transform transition-all duration-500 hover:scale-105 hover:shadow-lg group overflow-hidden"
              >
                {/* Effet de dégradé au survol */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#4f837d]/20 to-[#2e79b9]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#4f837d] to-[#2e79b9] flex items-center justify-center text-white shadow-md transition-transform duration-500 group-hover:scale-110">
                  {icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;